import React, { forwardRef } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/shift-away.css';
import { useTranslation } from 'react-i18next';

// React 19 removed element.ref access; Tippy needs a forwardRef child to attach its ref cleanly.
const TippyChild = forwardRef((props, ref) => <div ref={ref} {...props} />);

const YearlyCalendar = ({ year, aqiData }) => {
  const { t } = useTranslation();

  const months = [
    t('annualAQI.january'),
    t('annualAQI.february'),
    t('annualAQI.march'),
    t('annualAQI.april'),
    t('annualAQI.may'),
    t('annualAQI.june'),
    t('annualAQI.july'),
    t('annualAQI.august'),
    t('annualAQI.september'),
    t('annualAQI.october'),
    t('annualAQI.november'),
    t('annualAQI.december'),
  ];

  const daysOfWeek = [
    t('annualAQI.monday'),
    t('annualAQI.tuesday'),
    t('annualAQI.wednesday'),
    t('annualAQI.thursday'),
    t('annualAQI.friday'),
    t('annualAQI.saturday'),
    t('annualAQI.sunday'),
  ];

  const aqiMap = aqiData.reduce((acc, item) => {
    const date = new Date(item.date);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      '0'
    )}-${String(date.getDate()).padStart(2, '0')}`;
    acc[key] = { color: item.overall_color, aqiinfo: item };
    return acc;
  }, {});

  const generateDays = (month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfWeek = new Date(year, month, 1).getDay();
    const startIndex = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    return { daysInMonth, startIndex };
  };

  // Returns true for light background colors that need dark text
  const isLightColor = (color) => {
    if (!color) return false;
    const lightColors = ['yellow', '#ffd700', '#ffff00', '#ffeb3b', '#ffc107', '#ff0', 'gold'];
    const lower = color.toLowerCase();
    if (lightColors.some((c) => lower === c)) return true;
    // Parse hex and check luminance
    const hex = lower.replace('#', '');
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      // Perceived luminance formula
      return (r * 299 + g * 587 + b * 114) / 1000 > 160;
    }
    return false;
  };

  const getAQIData = (day, month) => {
    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(
      day
    ).padStart(2, '0')}`;
    return (
      aqiMap[dateKey] || {
        color: 'gray',
        aqiinfo: {
          overall_aqi: 'N/A',
          overall_category: t('annualAQI.no_data'),
          aqi_results: [],
          concentrations: [],
        },
      }
    );
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {months.map((month, index) => {
          const { daysInMonth, startIndex } = generateDays(index);
          return (
            <div
              key={month}
              className="w-full rounded-xl border border-gray-100 bg-white p-3 shadow-sm dark:border-dark-600 dark:bg-dark-800"
            >
              {/* Month header */}
              <div className="mb-2 border-b border-gray-100 pb-2 dark:border-dark-600">
                <h3 className="text-center text-sm font-medium uppercase tracking-wider text-gray-600 dark:text-dark-200">
                  {month}
                </h3>
              </div>

              {/* Day-of-week labels */}
              <div className="mb-1 grid grid-cols-7">
                {daysOfWeek.map((day, i) => (
                  <div
                    key={i}
                    className="pb-1 text-center text-xs font-medium text-gray-400 dark:text-dark-400"
                  >
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-px">
                {Array.from({ length: startIndex }).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square" />
                ))}

                {Array.from({ length: daysInMonth }, (_, i) => {
                  const { color, aqiinfo } = getAQIData(i + 1, index);
                  const hasData = aqiinfo.overall_aqi !== 'N/A';

                  const DayContent = (
                    <TippyChild
                      className={`aspect-square flex items-center justify-center rounded text-xs font-medium transition-all ${
                        hasData
                          ? 'cursor-pointer hover:scale-110 hover:opacity-90 hover:shadow-sm'
                          : 'bg-gray-50 text-gray-300 dark:bg-dark-700 dark:text-dark-600'
                      }`}
                      style={hasData ? { backgroundColor: color, color: isLightColor(color) ? '#1f2937' : 'white' } : {}}
                    >
                      {i + 1}
                    </TippyChild>
                  );

                  return hasData ? (
                    <Tippy
                      key={i + 1}
                      theme="calendar"
                      arrow={true}
                      animation="shift-away"
                      maxWidth={320}
                      content={
                        <div className="w-[300px] overflow-hidden rounded-xl">
                          {/* Colored header band */}
                          <div
                            className="flex items-center gap-3 px-4 py-3"
                            style={{ backgroundColor: color }}
                          >
                            <div
                              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white/20 text-sm font-medium"
                              style={{ color: isLightColor(color) ? '#1f2937' : 'white' }}
                            >
                              {aqiinfo.overall_aqi}
                            </div>
                            <div>
                              <p
                                className="text-sm font-medium leading-tight"
                                style={{ color: isLightColor(color) ? '#1f2937' : 'white' }}
                              >
                                {aqiinfo.overall_category}
                              </p>
                              <p
                                className="text-xs opacity-80"
                                style={{ color: isLightColor(color) ? '#374151' : 'white' }}
                              >
                                {`${year}-${String(index + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`}
                              </p>
                            </div>
                          </div>

                          {/* Body */}
                          <div className="px-4 py-3 space-y-3">
                            {/* AQI values */}
                            {aqiinfo.aqi_results?.length > 0 && (
                              <div>
                                <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-400">
                                  {t('annualAQI.aqi_values')}
                                </p>
                                <div className="grid grid-cols-3 gap-2">
                                  {aqiinfo.aqi_results.filter((aqi_result) => aqi_result && aqi_result.aqi != null && aqi_result.aqi !== -1).map((aqi_result, idx) => (
                                    <div
                                      key={idx}
                                      className="rounded-lg px-2 py-1.5 text-center"
                                      style={{ backgroundColor: aqi_result.color }}
                                    >
                                      <p
                                        className="truncate text-xs font-medium uppercase"
                                        style={{ color: isLightColor(aqi_result.color) ? '#374151' : 'white' }}
                                        title={aqi_result.pollutant}
                                      >
                                        {aqi_result.pollutant}
                                      </p>
                                      <p
                                        className="text-sm font-medium"
                                        style={{ color: isLightColor(aqi_result.color) ? '#111827' : 'white' }}
                                      >
                                        {aqi_result.aqi}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Concentration values */}
                            {aqiinfo.concentrations?.length > 0 && (
                              <div>
                                <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-400">
                                  {t('annualAQI.concentration_values')}
                                </p>
                                <div className="grid grid-cols-3 gap-2">
                                  {aqiinfo.concentrations.filter((concentration) => concentration && concentration.average_concentration != null && concentration.average_concentration > 0).map((concentration, idx) => (
                                    <div key={idx} className="rounded-lg bg-gray-50 px-2 py-1.5 text-center">
                                      <p className="truncate text-xs font-medium uppercase text-gray-400" title={concentration.symbol}>
                                        {concentration.symbol}
                                      </p>
                                      <p className="text-sm font-medium text-gray-800">
                                        {concentration.average_concentration}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        {concentration.unit}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      }
                    >
                      {DayContent}
                    </Tippy>
                  ) : (
                    <div key={i + 1}>{DayContent}</div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default YearlyCalendar;
